import gql from 'graphql-tag';

import { query, mutate } from '../graphql';

import GetRecipes from './graphql/queries/GetRecipes.graphql';
import GetRecipesCount from './graphql/queries/GetRecipesCount.graphql';
import GetRecipe from './graphql/queries/GetRecipe.graphql';
import GetRecipeReferenceData from './graphql/queries/GetRecipeReferenceData.graphql';
import GetRecipeIngredients from './graphql/queries/GetRecipeIngredients.graphql';
import RecipeFragment from './graphql/fragments/recipe.graphql';
import RecipeDetailFragment from './graphql/fragments/recipeDetail.graphql';
import RecipeIngredientFragment from './graphql/fragments/recipeIngredient.graphql';
import SaveRecipeMutation from './graphql/mutations/SaveRecipe.graphql';
import DeleteRecipeMutation from './graphql/mutations/DeleteRecipe.graphql';
import SaveRecipeCuisineMutation from './graphql/mutations/SaveRecipeCuisine.graphql';
import SaveRecipeMealTypeMutation from './graphql/mutations/SaveRecipeMealType.graphql';
import SaveRecipeMeasurementMutation from './graphql/mutations/SaveRecipeMeasurement.graphql';
import SaveRecipeUtensilMutation from './graphql/mutations/SaveRecipeUtensil.graphql';
import SaveRecipeHealthyLiveMutation from './graphql/mutations/SaveRecipeHealthyLive.graphql';
import SaveRecipeStepMutation from './graphql/mutations/SaveRecipeStep.graphql';
import SaveRecipeIngredientMutation from './graphql/mutations/SaveRecipeIngredient.graphql';
import SaveRecipeRayonMutation from './graphql/mutations/SaveRecipeRayon.graphql';
import SaveRecipeBrandMutation from './graphql/mutations/SaveRecipeBrand.graphql';
import GetRecipeBrands from './graphql/queries/GetRecipeBrands.graphql';
import RecipeBrandFragment from './graphql/fragments/recipeBrand.graphql';
import SaveIngredientTypeMutation from './graphql/mutations/SaveIngredientType.graphql';
import CalculateIngredientNutritionMutation from './graphql/mutations/CalculateIngredientNutrition.graphql';
import CalculateRecipeNutritionMutation from './graphql/mutations/CalculateRecipeNutrition.graphql';
import ApplyRecipeNutritionMutation from './graphql/mutations/ApplyRecipeNutrition.graphql';
import {getApprovedSignatures} from "@/api/signature/SignatureApi";
import axios from "axios";

// Liste ve sayaç sorguları aynı RecipeFilterInput'u paylaşır.
const buildRecipesFilter = body => ({
  ...(body?.search ? { search: body.search } : {}),
  ...(body?.user ? { user: body.user } : {}),
  ...(body?.status !== undefined ? { status: body.status } : {}),
  ...(body?.isChiefSelection !== undefined ? { isChiefSelection: body.isChiefSelection } : {}),
  ...(body?.hasPreparationPhotos ? { hasPreparationPhotos: true } : {}),
  ...(!body?.hasPreparationPhotos && body?.hasNoPreparationPhotos ? { hasPreparationPhotos: false } : {}),
  ...(body?.categories?.length ? { categories: body.categories.map(String) } : {}),
  ...(body?.publishDate?.length ? { publishDate: body.publishDate } : {}),
});

const RecipeApi = () => {
  axios.defaults.baseURL = process.env.VUE_APP_RECIPE_API;
  const headers = {
    'Content-Type': 'application/json',
  };

  // TODO: headline old kalkınca silinecek
  const fetchRecipesApiFromRecipeApi = async (params, body) => {
    let queryString = null;

    if (params) {
      queryString = Object.keys(params)
        .map(paramKey => `${paramKey}=${params[paramKey].toString()}`)
        .join('&');
    }

    let result;

    try {
      const fetchRecipes = (await axios.post(`/recipeSearch${queryString ? `?${queryString}` : ''}`, body, {
        headers,
      })).data;

      result = fetchRecipes;
    } catch (e) {
      result = [];
    }

    return result;
  };

  // TODO: headline old kalkınca silinecek
  const fetchAuthorsApi = async () => {
    try {
      const signatures = await getApprovedSignatures();

      return signatures || [];
    } catch (e) {
      return [];
    }
  };

  const fetchRecipesApi = async (params, body, options = {}) => {
    // getPendingContents listeyi tek moda kilitler (sadece yayinlanmislar ya da
    // sadece ileri tarihliler); includePendingContents ise tarih penceresini
    // tamamen kaldirip ikisini birlikte getirir.
    const { getPendingContents, includePendingContents } = options;
    const page = params?.page || 1;
    const limit = params?.take || 20;
    const filter = buildRecipesFilter(body);

    try {
      const { recipes } = await query(gql`
        ${GetRecipes}
        ${RecipeFragment}
      `, {
        filter, page, limit, getPendingContents, includePendingContents,
      });

      return recipes || [];
    } catch (e) {
      return [];
    }
  };

  // Liste sayfasındaki "toplam içerik" sayacı; fetchRecipesApi ile aynı filtreyi kullanır.
  // `limit`: sunucu sayımı bu eşikte durdurur (dönen değer eşiğe eşitse "N+").
  const fetchRecipesCountApi = async (body, options = {}) => {
    const { getPendingContents, limit } = options;
    const filter = buildRecipesFilter(body);

    try {
      const { recipesCount } = await query(gql`
        ${GetRecipesCount}
      `, { filter, getPendingContents, limit });

      return recipesCount ?? null;
    } catch (e) {
      return null;
    }
  };

  const fetchRecipeApi = async id => {
    try {
      const { recipe } = await query(gql`
        ${GetRecipe}
        ${RecipeDetailFragment}
      `, { id: parseInt(id, 10) });

      return recipe || null;
    } catch (e) {
      return null;
    }
  };

  const saveRecipeApi = async input => {
    const { saveRecipe } = await mutate(gql`
      ${SaveRecipeMutation}
      ${RecipeDetailFragment}
    `, { input });

    return saveRecipe;
  };

  const deleteRecipeApi = async id => {
    const { deleteRecipe } = await mutate(gql`
      ${DeleteRecipeMutation}
    `, { id: parseInt(id, 10) });

    return deleteRecipe;
  };

  const fetchReferenceDataApi = async (filterParam = {}) => {
    try {
      const filter = {};

      if (filterParam?.search) filter.search = filterParam.search;

      if (filterParam?.isApproved !== undefined) filter.isApproved = filterParam.isApproved;
      const variables = Object.keys(filter).length ? { filter } : {};
      const data = await query(gql`${GetRecipeReferenceData}`, variables);

      return {
        cuisines: data.recipeCuisines || [],
        mealTypes: data.recipeMealTypes || [],
        measurements: data.recipeMeasurements || [],
        utensils: data.recipeUtensils || [],
        healthyLives: data.recipeHealthyLives || [],
        steps: data.recipeSteps || [],
        rayons: data.recipeRayons || [],
        ingredientTypes: data.ingredientTypes || [],
      };
    } catch (e) {
      return {};
    }
  };

  const fetchRecipeIngredientsApi = async (params, body) => {
    const page = params?.page || 1;
    const limit = params?.take || 20;

    const filter = {
      ...(body?.search ? { search: body.search } : {}),
      ...(body?.isApproved !== undefined ? { isApproved: body.isApproved } : {}),
    };

    try {
      const { recipeIngredients } = await query(gql`
        ${GetRecipeIngredients}
        ${RecipeIngredientFragment}
      `, { filter, page, limit });

      return recipeIngredients || [];
    } catch (e) {
      return [];
    }
  };

  const saveRecipeCuisineApi = async input => {
    const { saveRecipeCuisine } = await mutate(gql`${SaveRecipeCuisineMutation}`, { input });

    return saveRecipeCuisine;
  };

  const saveRecipeMealTypeApi = async input => {
    const { saveRecipeMealType } = await mutate(gql`${SaveRecipeMealTypeMutation}`, { input });

    return saveRecipeMealType;
  };

  const saveRecipeMeasurementApi = async input => {
    const { saveRecipeMeasurement } = await mutate(gql`${SaveRecipeMeasurementMutation}`, { input });

    return saveRecipeMeasurement;
  };

  const saveRecipeUtensilApi = async input => {
    const { saveRecipeUtensil } = await mutate(gql`${SaveRecipeUtensilMutation}`, { input });

    return saveRecipeUtensil;
  };

  const saveRecipeHealthyLiveApi = async input => {
    const { saveRecipeHealthyLive } = await mutate(gql`${SaveRecipeHealthyLiveMutation}`, { input });

    return saveRecipeHealthyLive;
  };

  const saveRecipeStepApi = async input => {
    const { saveRecipeStep } = await mutate(gql`${SaveRecipeStepMutation}`, { input });

    return saveRecipeStep;
  };

  const saveRecipeIngredientApi = async input => {
    const { saveRecipeIngredient } = await mutate(gql`${SaveRecipeIngredientMutation}`, { input });

    return saveRecipeIngredient;
  };

  const saveRecipeRayonApi = async input => {
    const { saveRecipeRayon } = await mutate(gql`${SaveRecipeRayonMutation}`, { input });

    return saveRecipeRayon;
  };

  const saveRecipeBrandApi = async input => {
    const { saveRecipeBrand } = await mutate(gql`${SaveRecipeBrandMutation}`, { input });

    return saveRecipeBrand;
  };

  const saveIngredientTypeApi = async input => {
    const { saveIngredientType } = await mutate(gql`${SaveIngredientTypeMutation}`, { input });

    return saveIngredientType;
  };

  const calculateIngredientNutritionApi = async input => {
    const { calculateIngredientNutrition } = await mutate(gql`${CalculateIngredientNutritionMutation}`, { input });

    return calculateIngredientNutrition;
  };

  const calculateRecipeNutritionApi = async input => {
    const { calculateRecipeNutrition } = await mutate(gql`${CalculateRecipeNutritionMutation}`, { input });

    return calculateRecipeNutrition;
  };

  const applyRecipeNutritionApi = async input => {
    const { applyRecipeNutrition } = await mutate(gql`${ApplyRecipeNutritionMutation}`, { input });

    return applyRecipeNutrition;
  };

  const fetchRecipeBrandsApi = async (filterParam = {}) => {
    try {
      const filter = {};

      if (filterParam?.search) filter.search = filterParam.search;

      if (filterParam?.isApproved !== undefined) filter.isApproved = filterParam.isApproved;
      const variables = Object.keys(filter).length ? { filter } : {};
      const { recipeBrands } = await query(gql`
        ${GetRecipeBrands}
        ${RecipeBrandFragment}
      `, variables);

      return recipeBrands || [];
    } catch (e) {
      return [];
    }
  };

  return {
    fetchRecipesApi,
    fetchRecipesCountApi,
    fetchRecipeApi,
    fetchReferenceDataApi,
    fetchRecipeIngredientsApi,
    saveRecipeApi,
    deleteRecipeApi,
    saveRecipeCuisineApi,
    saveRecipeMealTypeApi,
    saveRecipeMeasurementApi,
    saveRecipeUtensilApi,
    saveRecipeHealthyLiveApi,
    saveRecipeStepApi,
    saveRecipeIngredientApi,
    saveRecipeRayonApi,
    saveRecipeBrandApi,
    fetchRecipeBrandsApi,
    saveIngredientTypeApi,
    fetchRecipesApiFromRecipeApi,
    fetchAuthorsApi,
    calculateIngredientNutritionApi,
    calculateRecipeNutritionApi,
    applyRecipeNutritionApi,
  };
};

export default RecipeApi;
